import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Trash2, Clock, Globe, CheckCircle, XCircle, History as HistoryIcon } from 'lucide-react';
import { searchHistoryService } from '../../services/searchHistory';
import HistoryModal from '../../components/common/HistoryModal';

const HistoryPage = () => {
  const navigate = useNavigate();
  const [searchHistory, setSearchHistory] = useState([]);
  const [selectedHistoryItem, setSelectedHistoryItem] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = () => {
    const history = searchHistoryService.getHistory();
    setSearchHistory(history);
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  const handleHistoryItemClick = (historyItem) => {
    setSelectedHistoryItem(historyItem);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedHistoryItem(null);
  };

  const handleDeleteHistoryItem = (id, e) => {
    e.stopPropagation(); // Prevent triggering the item click
    searchHistoryService.removeFromHistory(id);
    loadHistory();
  };

  const handleClearAllHistory = () => {
    if (window.confirm('Are you sure you want to clear all search history?')) {
      searchHistoryService.clearHistory();
      loadHistory();
    }
  };

  const formatUrl = (url) => {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname.replace('www.', '');
    } catch {
      return url;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  };

  return (
    <div style={{ backgroundColor: '#FFFFFF', minHeight: '100vh' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '32px 24px' }}>
        {/* Header with back navigation */}
        <div style={{ marginBottom: '32px' }}>
          <button
            onClick={handleBackToHome}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              color: '#3ECC71',
              fontWeight: 500,
              cursor: 'pointer',
              fontSize: '14px',
              marginBottom: '24px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
            }}
            onMouseEnter={(e) => {
              e.target.style.color = '#2EAC51';
            }}
            onMouseLeave={(e) => {
              e.target.style.color = '#3ECC71';
            }}
          >
            <ArrowLeft size={18} />
            Back to Home
          </button>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <HistoryIcon size={28} style={{ color: '#6B7785' }} />
              <h1 style={{
                fontSize: '30px',
                lineHeight: '1.07',
                fontWeight: 600,
                color: '#000000',
                letterSpacing: '-0.01em',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                margin: 0
              }}>
                Search History
              </h1>
            </div>
            {searchHistory.length > 0 && (
              <button
                onClick={handleClearAllHistory}
                style={{
                  backgroundColor: 'transparent',
                  border: '1px solid #EF4444',
                  color: '#EF4444',
                  fontWeight: 500,
                  borderRadius: '8px',
                  padding: '8px 16px',
                  fontSize: '14px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#FEF2F2';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                }}
              >
                <Trash2 size={16} />
                Clear All
              </button>
            )}
          </div>
          
          <p style={{
            fontSize: '16px',
            color: '#6B7785',
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
            margin: 0
          }}>
            View and manage your previous Tabby website checks
          </p>
        </div>

        {/* History List */}
        {searchHistory.length === 0 ? (
          <div style={{ 
            textAlign: 'center',
            padding: '48px 24px',
            backgroundColor: '#F9FAFB',
            borderRadius: '12px',
            border: '2px dashed #E5E7EB'
          }}>
            <HistoryIcon size={48} style={{ color: '#9CA3AF', marginBottom: '16px' }} />
            <h3 style={{
              color: '#6B7785',
              fontSize: '18px',
              fontWeight: 500,
              margin: '0 0 8px 0',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
            }}>
              No search history yet
            </h3>
            <p style={{
              color: '#9CA3AF',
              fontSize: '14px',
              margin: 0,
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
            }}>
              Your website checks will appear here for easy reference
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {searchHistory.map((item) => (
              <div 
                key={item.id}
                onClick={() => handleHistoryItemClick(item)}
                style={{ 
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #E5E7EB',
                  borderRadius: '12px',
                  padding: '20px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = '#3ECC71';
                  e.target.style.backgroundColor = '#F9FAFB';
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = '#E5E7EB';
                  e.target.style.backgroundColor = '#FFFFFF';
                }}
              >
                {/* Result Icon */}
                <div style={{ flexShrink: 0 }}>
                  {item.hasTabby ? (
                    <CheckCircle size={24} style={{ color: '#368510' }} />
                  ) : (
                    <XCircle size={24} style={{ color: '#e31e18' }} />
                  )}
                </div>

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <Globe size={16} style={{ color: '#6B7785' }} />
                    <h3 style={{
                      fontSize: '16px',
                      fontWeight: 600,
                      color: '#1A202C',
                      margin: 0,
                      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {formatUrl(item.url)}
                    </h3>
                  </div>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '8px' }}>
                    <span style={{ 
                      color: item.hasTabby ? '#368510' : '#e31e18',
                      fontSize: '14px',
                      fontWeight: 500
                    }}>
                      {item.hasTabby ? 'Tabby Available' : 'Tabby Not Found'}
                    </span>
                    <span style={{ 
                      color: '#6B7785',
                      fontSize: '14px'
                    }}>
                      {item.confidence}% confidence
                    </span>
                  </div>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Clock size={14} style={{ color: '#9CA3AF' }} />
                    <span style={{ 
                      color: '#9CA3AF',
                      fontSize: '12px',
                      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
                    }}>
                      {formatDate(item.searchedAt)}
                    </span>
                  </div>
                </div>

                {/* Delete Button */}
                <button
                  onClick={(e) => handleDeleteHistoryItem(item.id, e)}
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: '#9CA3AF',
                    cursor: 'pointer',
                    padding: '8px',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.color = '#EF4444';
                    e.target.style.backgroundColor = '#FEF2F2';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.color = '#9CA3AF';
                    e.target.style.backgroundColor = 'transparent';
                  }}
                >
                  <Trash2 size={18} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* History Detail Modal */}
      <HistoryModal
        historyItem={selectedHistoryItem}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </div>
  );
};

export default HistoryPage;
